import { UzButton, UzCard } from '@utazzo/ui';
import { Download } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import {
  downloadExcel,
  downloadPdf,
  type CsvColumn,
} from '@/components/list-screen/list-screen-utils';
import { PageShell } from '@/components/page-shell';

/**
 * Shared shell for the MYTAdmin-style finance / sales reports (one row per
 * record, with a totals footer and Excel + PDF export of everything). It is the
 * common foundation the report pages are built on so each report page only
 * declares its columns, filters and data hook.
 *
 * A report fetches its full (unpaginated) result set because the totals and the
 * export must cover every matching row - the same pattern as the shipped
 * Supplier Payment Status / GST register screens.
 */

/** One report column. Extends the CSV/Excel `{ header, value }` spec (value is
 *  the raw, exportable cell) with display-only concerns. */
export interface ReportColumn<TRow> extends CsvColumn<TRow> {
  /** Cell + header alignment. Defaults to left. */
  align?: 'left' | 'right' | 'center';
  /** Rich display node; falls back to `value(row)` when omitted. */
  render?: (row: TRow) => React.ReactNode;
  /** Extra classes on the body cell. */
  className?: string;
  /** Footer total cell for this column; omit for a blank footer cell. */
  total?: (rows: ReadonlyArray<TRow>) => React.ReactNode;
}

export interface ReportListScreenProps<TRow> {
  title: string;
  description?: string;
  columns: ReadonlyArray<ReportColumn<TRow>>;
  rows: TRow[];
  getRowKey: (row: TRow, index: number) => React.Key;
  loading?: boolean;
  error?: boolean;
  /** Message shown when there are no rows. */
  emptyMessage?: string;
  /** Free-text search input, rendered above the filters. */
  search?: React.ReactNode;
  /** Filter controls (chips / selects / date range) rendered above the table. */
  filters?: React.ReactNode;
  /** Base filename for the Excel / PDF export. */
  exportName: string;
  /**
   * Source of the rows to export. Defaults to the on-screen `rows`. Supply this
   * only when export must pull a different/bigger set than what is rendered.
   */
  exportRows?: () => Promise<TRow[]> | TRow[];
  /** Extra toolbar actions rendered left of the export buttons. */
  actions?: React.ReactNode;
  onRowClick?: (row: TRow) => void;
}

const alignClass = (a?: 'left' | 'right' | 'center'): string =>
  a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';

export function ReportListScreen<TRow>({
  title,
  description,
  columns,
  rows,
  getRowKey,
  loading,
  error,
  emptyMessage = 'No rows match these filters.',
  search,
  filters,
  exportName,
  exportRows,
  actions,
  onRowClick,
}: ReportListScreenProps<TRow>): React.JSX.Element {
  const [exporting, setExporting] = useState(false);
  const hasFooter = columns.some((c) => typeof c.total === 'function');

  const handleExport = async (fmt: 'xlsx' | 'pdf'): Promise<void> => {
    setExporting(true);
    try {
      const data = exportRows ? await exportRows() : rows;
      if (fmt === 'pdf') downloadPdf(data, columns, exportName, title);
      else downloadExcel(data, columns, exportName);
    } catch {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  return (
    <PageShell
      title={title}
      description={description}
      actions={
        <div className="flex items-center gap-2">
          {actions}
          <UzButton
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            disabled={exporting || rows.length === 0}
            onClick={() => void handleExport('pdf')}
          >
            PDF
          </UzButton>
          <UzButton
            variant="outline"
            leftIcon={<Download className="h-4 w-4" />}
            disabled={exporting || rows.length === 0}
            onClick={() => void handleExport('xlsx')}
          >
            Excel
          </UzButton>
        </div>
      }
    >
      {search && <div className="mb-3">{search}</div>}
      {filters && <div className="mb-4 flex flex-wrap items-end gap-3">{filters}</div>}

      <UzCard padding="none">
        {loading ? (
          <p className="p-6 text-sm text-zinc-500">Loading report...</p>
        ) : error ? (
          <p className="p-6 text-sm text-red-600">Could not load the report.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-zinc-500">{emptyMessage}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                  {columns.map((c, i) => (
                    <th key={i} className={`px-3 py-3 ${alignClass(c.align)}`}>
                      {c.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr
                    key={getRowKey(row, i)}
                    className={`border-b border-zinc-100 last:border-0 ${
                      onRowClick ? 'cursor-pointer hover:bg-zinc-50' : ''
                    }`}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {columns.map((c, j) => (
                      <td
                        key={j}
                        className={`px-3 py-3 text-zinc-700 ${alignClass(c.align)} ${
                          c.align === 'right' ? 'tabular-nums' : ''
                        } ${c.className ?? ''}`}
                      >
                        {c.render ? c.render(row) : String(c.value(row) ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {hasFooter && (
                <tfoot>
                  <tr className="border-t border-zinc-200 bg-zinc-50 text-sm font-semibold text-zinc-900">
                    {columns.map((c, i) => (
                      <td
                        key={i}
                        className={`px-3 py-3 ${alignClass(c.align)} ${
                          c.align === 'right' ? 'tabular-nums' : ''
                        }`}
                      >
                        {c.total ? c.total(rows) : ''}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </UzCard>
    </PageShell>
  );
}
